// src/data/helpCenterContent.js
// Help Center content — 12 categories (6 practitioner, 6 seeker), 40 articles total
// Practitioner: 18 articles | Seeker: 22 articles

import {
  Rocket, Layout, Calendar, Users, Shield, Wrench, Globe,
  Search, CreditCard, ArrowUpCircle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Practitioner categories (audience: 'practitioner')
// ---------------------------------------------------------------------------

export const HELP_CATEGORIES = [
  {
    id: 'getting-set-up',
    slug: 'getting-set-up',
    title: 'Getting Set Up',
    description: 'Set up your account and get your practice online',
    Icon: Rocket,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    audience: 'practitioner',
    articles: [
      {
        id: 'creating-your-account',
        title: 'Creating your account',
        audience: 'practitioner',
        keywords: ['sign up', 'register', 'email', 'verification', 'password', 'new account'],
        searchMetadata: {
          aiKeywords: ['signup', 'sign-up', 'create account', 'new practitioner', 'get started', 'join', 'registration', 'onboarding', 'verify email', 'confirmation link', 'activate account', 'practitioner dashboard', 'first login', 'welcome', 'set up'],
          intentPhrases: ['how do I create an account', 'how do I sign up', 'how to register as a practitioner', 'how do I get started', 'where do I sign up', 'how to verify my email', 'what happens after I sign up'],
          tags: ['account', 'onboarding', 'getting-started'],
        },
        paragraphs: [
          'To create your account, go to divineframeworks.com and click "Get Started." You\'ll enter your email address and create a password — that\'s all it takes to get in the door.',
          'After signing up, a verification email will be sent to the address you provided. Click the confirmation link to activate your account. If it doesn\'t arrive within a few minutes, check your spam or promotions folder.',
          'Once verified, you\'ll be guided through a short onboarding flow where you\'ll share your name, your primary service type, and a few details about your practice. This takes about 3–5 minutes and sets up your profile for the first time.',
          'After onboarding, you\'ll land on your practitioner dashboard — the home base for running your practice. Your account is active right away, and you can start building your site immediately.',
        ],
      },
      {
        id: 'completing-your-profile',
        title: 'Completing your profile',
        audience: 'practitioner',
        keywords: ['profile', 'bio', 'photo', 'services', 'display name', 'onboarding'],
        searchMetadata: {
          aiKeywords: ['profile setup', 'practitioner bio', 'headshot', 'profile photo', 'display name', 'service type', 'about me', 'profile picture', 'avatar', 'practitioner description', 'write bio', 'add photo', 'update profile', 'profile settings', 'public profile'],
          intentPhrases: ['how do I complete my profile', 'how to write a good bio', 'what size should my photo be', 'how to add my services to my profile', 'where do I update my display name', 'how to choose my service types'],
          tags: ['profile', 'onboarding', 'getting-started'],
        },
        paragraphs: [
          'Your profile is the foundation of your presence on Divine Frameworks. Head to Settings > Public Profile to fill in your display name, bio, and photo. A complete profile builds trust with prospective clients before they even reach out.',
          'Choosing your services helps clients understand what you offer. You can select from a list of common services — like Tarot, Reiki, or Life Coaching — or add custom services that fit your unique work. Your primary service appears on your listing in the directory.',
          'Your bio is often the first thing a potential client reads. Write it in first person, keep it warm and authentic, and focus on how you help people — not just your credentials or background. You can update it anytime without republishing your site.',
          'A professional photo makes a real difference in how your profile lands. A clear headshot with good lighting works best — you don\'t need a studio shot. Square images work best for the profile circle. Aim for at least 400×400 pixels.',
        ],
      },
      {
        id: 'publishing-your-first-website',
        title: 'Publishing your first website',
        audience: 'practitioner',
        keywords: ['publish', 'website', 'site editor', 'go live', 'url', 'slug', 'share link'],
        searchMetadata: {
          aiKeywords: ['launch website', 'go live', 'publish site', 'site URL', 'custom URL', 'website address', 'share link', 'live site', 'first publish', 'site editor', 'build website', 'create website', 'make my site live', 'website builder', 'choose URL'],
          intentPhrases: ['how do I publish my website', 'how to go live', 'how to get my site URL', 'how to share my website', 'where do I publish', 'how to launch my site', 'what do I need before publishing'],
          tags: ['website', 'publishing', 'site-editor'],
        },
        paragraphs: [
          'Open the Site Editor from your dashboard or navigation menu. The editor shows you a live preview of your site on the right as you make changes on the left — what you see is exactly what your visitors will see.',
          'Start with the essentials: add a headline in the Hero section, choose a color scheme in Design, and add at least one service. These three steps alone give you a solid first site to publish.',
          'When you\'re ready to go live, click "Publish" in the top bar. If you haven\'t chosen a URL yet, you\'ll be prompted to pick one. Your site address will be divineframeworks.com/site/your-chosen-name — keep it simple and professional.',
          'Once published, a "View Live Site" link appears in the editor\'s top bar. Share your URL anywhere — your Instagram bio, email signature, or business card. Your site stays live until you choose to take it down, and you can update content anytime without losing your published address.',
        ],
      },
    ],
  },

  {
    id: 'site-editor',
    slug: 'site-editor',
    title: 'Site Editor',
    description: 'Build and customize your practitioner website',
    Icon: Layout,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    audience: 'practitioner',
    articles: [
      {
        id: 'designing-your-website',
        title: 'Designing your website',
        audience: 'practitioner',
        keywords: ['design', 'colors', 'fonts', 'layout', 'template', 'color scheme', 'theme'],
        searchMetadata: {
          aiKeywords: ['website design', 'color scheme', 'color palette', 'font pairing', 'typography', 'layout template', 'visual identity', 'branding', 'customize appearance', 'design panel', 'site theme', 'change colors', 'change fonts', 'site look', 'aesthetic'],
          intentPhrases: ['how do I change my site colors', 'how to pick a font', 'how to change my website design', 'where are the design settings', 'how to customize my site layout', 'can I change my color scheme'],
          tags: ['design', 'site-editor', 'customization'],
        },
        paragraphs: [
          'The Design panel in the Site Editor is where you control your site\'s visual identity. Click "Design" in the left panel to access color schemes, font pairings, and layout options. Every change previews instantly on the right.',
          'Color schemes set your primary color (used on buttons and highlights), your background tone, and text contrast — all automatically balanced for readability. You can choose a preset scheme or fine-tune individual colors to match your brand.',
          'Font pairings are selected to work beautifully together: a display font for headings and a clean body font for longer text. Pick the pairing that matches your personality — from editorial and refined to warm and approachable.',
          'Layout templates control how your homepage sections are arranged on the page. You can preview each option instantly and switch at any time — your content carries over, only the structure changes.',
        ],
      },
      {
        id: 'adding-your-services',
        title: 'Adding your services',
        audience: 'practitioner',
        keywords: ['services', 'offering', 'pricing', 'duration', 'booking', 'add service'],
        searchMetadata: {
          aiKeywords: ['create service', 'add offering', 'service listing', 'set price', 'session duration', 'service description', 'service photo', 'service image', 'new service', 'edit service', 'remove service', 'service card', 'bookable service', 'what I offer'],
          intentPhrases: ['how do I add a service', 'how to set my prices', 'how to create a new offering', 'how to add a photo to my service', 'can I have multiple services', 'how to edit a service description'],
          tags: ['services', 'site-editor', 'pricing'],
        },
        paragraphs: [
          'Open the Services panel in the Site Editor and click "Add Service" to create your first offering. Each service has a name, a short description, a duration, and an optional price.',
          'Write service descriptions that speak to outcomes, not just process. "A 60-minute Reiki session to help you release tension and restore inner balance" connects with clients far better than "60-minute Reiki session." Be specific about what someone will experience or gain.',
          'You can add as many services as you offer. Each service appears on your site, and once Stripe is connected, clients can book and pay directly from your service listing.',
          'To add a photo to a service, click the image icon when editing a service card. A warm, atmospheric image that evokes the feeling of the session helps clients connect emotionally with what you\'re offering.',
        ],
      },
      {
        id: 'customizing-hero-section',
        title: 'Customizing your hero section',
        audience: 'practitioner',
        keywords: ['hero', 'background', 'headline', 'banner', 'image', 'gradient', 'cta', 'button'],
        searchMetadata: {
          aiKeywords: ['hero banner', 'hero image', 'background image', 'headline text', 'call to action', 'CTA button', 'landing page', 'header section', 'site banner', 'hero background', 'Unsplash', 'stock photo', 'gradient background', 'upload image', 'first impression'],
          intentPhrases: ['how do I change my hero image', 'how to edit the top of my site', 'how to change my headline', 'how to add a background image', 'how to customize the banner', 'where do I edit the call to action button'],
          tags: ['hero', 'site-editor', 'design'],
        },
        paragraphs: [
          'The hero section is the first thing visitors see when they arrive at your site — it sets the tone for everything that follows. Open the Hero panel in the Site Editor to customize the background, headline, and call-to-action button.',
          'You can choose from three background types: a curated stock photo from Unsplash, your own uploaded image, or a gradient that uses your site\'s color scheme. Stock photos are high-quality, fast-loading, and a great option if you don\'t have the right image yet.',
          'Your headline should tell visitors who you are and who you help. Keep it to one or two sentences. Examples: "Guiding you back to yourself through Tarot and intuition" or "Reiki healing for the modern seeker." Specific always outperforms vague.',
          'The call-to-action button ("Book a Session," "Learn More," etc.) can be renamed and linked to your booking page or any section on your site. Make sure it\'s pointing somewhere meaningful — this is often the first thing a visitor clicks.',
        ],
      },
    ],
  },

  {
    id: 'bookings-payments',
    slug: 'bookings-payments',
    title: 'Bookings & Payments',
    description: 'Manage your availability, bookings, and payments',
    Icon: Calendar,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    audience: 'practitioner',
    articles: [
      {
        id: 'setting-up-availability',
        title: 'Setting up your availability',
        audience: 'practitioner',
        keywords: ['availability', 'hours', 'schedule', 'timezone', 'buffer', 'blocked dates', 'working hours'],
        searchMetadata: {
          aiKeywords: ['set hours', 'working hours', 'schedule settings', 'time zone', 'buffer time', 'block dates', 'vacation', 'day off', 'available times', 'booking window', 'weekly schedule', 'availability settings', 'open slots', 'office hours', 'break between sessions'],
          intentPhrases: ['how do I set my availability', 'how to block dates', 'how to add buffer time', 'how to change my working hours', 'how to set my timezone', 'how do clients see my available times', 'how to take time off'],
          tags: ['availability', 'scheduling', 'settings'],
        },
        paragraphs: [
          'Before clients can book with you, set your weekly availability. Go to Settings > Availability & Scheduling to define your working days and hours. This is what clients see when they choose a session time.',
          'For each day you work, set your start and end times. You can have different hours on different days — for example, 9am–5pm Monday through Wednesday and 10am–3pm on Fridays. Days you leave off are automatically marked as unavailable.',
          'Buffer time adds a gap between sessions — useful if you need time to ground yourself, write notes, or simply breathe between clients. Even 15 minutes between sessions makes a meaningful difference over a full day.',
          'Block out specific dates for vacations, personal commitments, or anything that takes you offline. Blocked dates prevent bookings during those times regardless of your regular availability settings.',
          'Make sure your timezone is set correctly at the top of the Availability page. Clients see session times in their own timezone automatically, but your hours are managed in yours — so an incorrect setting will cause mismatches.',
        ],
      },
      {
        id: 'connecting-google-calendar',
        title: 'Connecting your calendar',
        audience: 'practitioner',
        keywords: ['google calendar', 'calendar sync', 'integration', 'connect', 'ical', 'sync'],
        searchMetadata: {
          aiKeywords: ['Google Calendar', 'calendar integration', 'sync bookings', 'connect calendar', 'calendar link', 'auto sync', 'disconnect calendar', 'calendar connected', 'booking sync', 'schedule sync', 'gcal', 'Google account', 'calendar settings'],
          intentPhrases: ['how do I connect Google Calendar', 'how to sync my bookings to my calendar', 'how to disconnect Google Calendar', 'why aren\'t bookings showing on my calendar', 'does it sync both ways', 'how to link my calendar'],
          tags: ['calendar', 'integrations', 'scheduling'],
        },
        paragraphs: [
          'Connecting your Google Calendar keeps your schedule in sync without any extra effort. When a client books a session, the appointment automatically appears on your Google Calendar — no copy-pasting required.',
          'To connect, go to Settings > Availability & Scheduling and click "Connect Google Calendar." You\'ll be redirected to sign in with Google and authorize calendar access. Once connected, new bookings sync within seconds.',
          'Calendar sync goes from Divine Frameworks to Google Calendar (not the other direction). If you need to block off personal time that appears on your Google Calendar, use the "Block Dates" feature in your Availability settings to match it manually.',
          'If you ever need to disconnect your calendar, go to the same settings page and click "Disconnect." Your booking history, availability settings, and existing appointments are all preserved — only the live sync stops going forward.',
        ],
      },
      {
        id: 'getting-paid-with-stripe',
        title: 'Getting paid with Stripe',
        audience: 'practitioner',
        keywords: ['stripe', 'payments', 'payout', 'bank account', 'revenue', 'commission', 'connect'],
        searchMetadata: {
          aiKeywords: ['Stripe Connect', 'payment setup', 'receive payments', 'bank deposit', 'payout schedule', 'payment processing', 'get paid', 'earnings', 'revenue', 'no commission', 'zero platform fee', 'Stripe dashboard', 'connect Stripe', 'payment method', 'accept payments'],
          intentPhrases: ['how do I get paid', 'how to connect Stripe', 'how to set up payments', 'does Divine Frameworks take a cut', 'when do I get my money', 'how to accept payments', 'how long until I get paid'],
          tags: ['payments', 'stripe', 'earnings'],
        },
        paragraphs: [
          'Divine Frameworks uses Stripe to process payments — the same secure infrastructure used by millions of businesses worldwide. You keep 100% of your booking revenue. Divine Frameworks does not take a commission on what you earn.',
          'To set up payments, go to Settings > Business & Payments and click "Connect with Stripe." You\'ll complete Stripe\'s verification process, which includes confirming your identity and adding a bank account for deposits.',
          'Once Stripe is connected, you can set prices on your services. Clients pay at the time of booking, and funds typically reach your bank account within 2–3 business days after a session payment is processed.',
          'Your full payout history and Stripe account details live directly in your Stripe dashboard. From your Earnings page on Divine Frameworks, you\'ll find a direct link to access it — everything in one place.',
        ],
      },
    ],
  },

  {
    id: 'managing-your-practice',
    slug: 'managing-your-practice',
    title: 'Managing Your Practice',
    description: 'Clients, messages, and day-to-day operations',
    Icon: Users,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    audience: 'practitioner',
    articles: [
      {
        id: 'viewing-your-clients',
        title: 'Viewing your clients',
        audience: 'practitioner',
        keywords: ['clients', 'client list', 'booking history', 'active', 'inactive', 'search clients'],
        searchMetadata: {
          aiKeywords: ['client management', 'client list', 'client profile', 'client history', 'active clients', 'inactive clients', 'find client', 'search client', 'client card', 'client records', 'client database', 'client overview', 'session history', 'booking count', 'client revenue'],
          intentPhrases: ['how do I see my clients', 'how to find a client', 'where is my client list', 'how to view client booking history', 'what does active and inactive mean', 'how are clients created'],
          tags: ['clients', 'practice-management', 'dashboard'],
        },
        paragraphs: [
          'The Clients page gives you a clear overview of everyone who has booked with you. Each client card shows their name, total number of bookings, total revenue, and when their last session was.',
          'Clients are automatically created when someone books a session with you — there\'s nothing to set up manually. They\'re grouped by email address, so if the same person books multiple times, you\'ll see a single record with their full history.',
          'A client is marked "Active" if they\'ve had a session within the past 90 days. Clients who haven\'t booked recently are marked "Inactive" — a useful signal for who you might want to reach out to or re-engage.',
          'Use the search bar to find a specific client by name or email. The sort and filter tools let you view your most frequent bookers, sort by total revenue, or filter by the services they\'ve booked.',
        ],
      },
      {
        id: 'using-messages',
        title: 'Using messages',
        audience: 'practitioner',
        keywords: ['messages', 'messaging', 'inbox', 'client messages', 'chat', 'reply'],
        searchMetadata: {
          aiKeywords: ['messaging', 'inbox', 'client conversation', 'send message', 'reply to client', 'unread messages', 'message notification', 'chat', 'communication', 'client contact', 'message history', 'conversation thread', 'contact client'],
          intentPhrases: ['how do I message a client', 'where are my messages', 'how to reply to a client', 'how does messaging work', 'can clients message me', 'how to check my inbox'],
          tags: ['messages', 'communication', 'practice-management'],
        },
        paragraphs: [
          'The Messages page is where all your client conversations live. When a client sends you a message through your published site or after booking a session, it shows up here as a conversation thread.',
          'Click any conversation to open it and read the full message history. Type your reply in the text field at the bottom and press Enter or click Send. Messages are delivered instantly.',
          'Conversations are sorted by most recent activity, so your active threads are always at the top. Unread messages show an indicator, and the total unread count appears in your navigation bar so you never miss something important.',
          'For clients who want to reach you outside of Divine Frameworks, make sure your email or preferred contact method is visible somewhere on your site — in your About section or your contact footer.',
        ],
      },
      {
        id: 'your-public-listing',
        title: 'Your public listing',
        audience: 'practitioner',
        keywords: ['directory', 'listing', 'discover', 'search', 'public profile', 'visibility'],
        searchMetadata: {
          aiKeywords: ['directory listing', 'practitioner directory', 'public profile', 'discovery', 'searchable', 'found by clients', 'directory visibility', 'listing status', 'appear in directory', 'hide listing', 'visible to clients', 'directory placement', 'get discovered'],
          intentPhrases: ['how do I appear in the directory', 'how do clients find me', 'how to improve my listing', 'can I hide my listing', 'what does my directory listing show', 'how to get more visibility'],
          tags: ['directory', 'visibility', 'marketing'],
        },
        paragraphs: [
          'When you publish your site, your profile is automatically included in the Divine Frameworks practitioner directory — a searchable listing that helps prospective clients discover you without needing to know your exact URL.',
          'Your listing shows your name, primary service, location (if you\'ve added it), and a brief excerpt from your bio. Clients can click through to your full published site from the directory.',
          'Your listing is tied to your site\'s published status. If your site is live, you appear in the directory. If you unpublish your site or pause your account, your listing is hidden until you\'re back online.',
          'To make the most of your directory listing, ensure your bio is complete, your services are described and priced, and your hero section has a strong image. A complete, polished profile builds trust before a client ever messages you.',
        ],
      },
    ],
  },

  {
    id: 'account-billing',
    slug: 'account-billing',
    title: 'Account & Billing',
    description: 'Subscription, account settings, and security',
    Icon: Shield,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    audience: 'practitioner',
    articles: [
      {
        id: 'subscription-tiers',
        title: 'Subscription tiers',
        audience: 'practitioner',
        keywords: ['subscription', 'plan', 'essentials', 'professional', 'premier', 'booking and directory', 'upgrade', 'pricing', 'tier'],
        searchMetadata: {
          aiKeywords: ['pricing plans', 'subscription cost', 'monthly fee', 'free plan', 'essentials plan', 'booking and directory plan', 'professional plan', 'premier plan', 'upgrade plan', 'downgrade plan', 'change plan', 'billing cycle', 'plan comparison', 'what plan', 'which tier', 'plan features'],
          intentPhrases: ['how much does it cost', 'what are the plans', 'how to upgrade my plan', 'what does the free plan include', 'can I downgrade', 'what is the difference between plans', 'which plan should I choose'],
          tags: ['billing', 'subscription', 'pricing'],
        },
        paragraphs: [
          'Divine Frameworks offers four plans: Essentials (Free), Booking & Directory ($29/month), Professional ($49/month), and Premier ($99/month). All plans give you access to the practitioner directory. The differences are in the depth of tools, customization, and support available.',
          'Essentials (Free) is the right place to start. You get a directory listing with a basic profile and up to 3 bookings per month — everything you need to test the waters.',
          'Booking & Directory ($29/month) gives you unlimited bookings, payment processing, client management, and Google Calendar sync. Everything you need to run your practice.',
          'Professional ($49/month) adds the full website builder with all templates and customization options, plus a verification badge on your profile. Already have a website? It works with your existing site too — add a booking button and manage everything from your DF dashboard.',
          'Premier ($99/month) includes everything above plus AI-powered session prep, practice coaching insights, and priority placement in the directory. It\'s designed for practitioners running a full-time, high-volume practice.',
          'To upgrade or view what\'s included in each plan, go to Settings > Account & Billing. Upgrades take effect immediately; downgrades apply at the end of your current billing period. You keep 100% of your booking revenue on every plan.',
        ],
      },
      {
        id: 'updating-your-account',
        title: 'Updating your account',
        audience: 'practitioner',
        keywords: ['email', 'password', 'account settings', 'update', 'change email', 'change password'],
        searchMetadata: {
          aiKeywords: ['change email', 'change password', 'update email', 'reset password', 'account settings', 'edit account', 'display name', 'login email', 'forgot password', 'verify email', 'account security', 'update login'],
          intentPhrases: ['how do I change my email', 'how to change my password', 'how to update my display name', 'where are my account settings', 'I forgot my password', 'how to update my account information'],
          tags: ['account', 'security', 'settings'],
        },
        paragraphs: [
          'To update your email address, go to Settings > Account & Security. You\'ll need to verify the new address before the change takes effect — confirmation links are sent to both your old and new email addresses.',
          'To change your password, use the "Change Password" option in Account & Security. You\'ll need to enter your current password first. If you\'ve forgotten your password, use the "Forgot Password" link on the login page to reset it.',
          'Your public display name — what clients see on your site and in the directory — is separate from your account login. Update it in Settings > Public Profile or directly in the Site Editor under your profile section.',
        ],
      },
      {
        id: 'pausing-or-deleting-your-account',
        title: 'Pausing or deleting your account',
        audience: 'practitioner',
        keywords: ['pause', 'delete', 'deactivate', 'close account', 'cancel', 'remove account'],
        searchMetadata: {
          aiKeywords: ['pause listing', 'hide profile', 'go offline', 'take a break', 'deactivate account', 'close account', 'delete account', 'remove profile', 'cancel account', 'unpause', 'reactivate', 'account deletion', 'data removal', 'temporary break'],
          intentPhrases: ['how do I pause my listing', 'how to delete my account', 'can I take a break', 'how to hide my profile', 'how to reactivate my account', 'what happens when I delete my account', 'how to go offline temporarily'],
          tags: ['account', 'pause', 'deletion'],
        },
        paragraphs: [
          'If you need a break, pausing your listing is a gentler option than deleting your account. Pausing hides your site from the public and removes you from the directory, but keeps all your content, settings, and client history intact. You can unpause at any time.',
          'To pause, go to Settings > Account & Security and select "Pause my listing." Your site goes offline immediately, but nothing is deleted. When you\'re ready to return, just unpause and you\'re live again.',
          'Permanently deleting your account removes all your data — your site, services, client records, and booking history. This action cannot be undone, so make sure to export any data you need first.',
          'To request account deletion, email us at support@divineframeworks.com. We\'ll verify your identity and process the deletion within 5 business days.',
        ],
      },
    ],
  },

  {
    id: 'troubleshooting',
    slug: 'troubleshooting',
    title: 'Troubleshooting',
    description: 'Solutions for common issues',
    Icon: Wrench,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    audience: 'practitioner',
    articles: [
      {
        id: 'images-not-showing',
        title: 'My images aren\'t showing on my published site',
        audience: 'practitioner',
        keywords: ['images', 'broken images', 'not loading', 'missing photo', 'upload', 'cache', 'republish'],
        searchMetadata: {
          aiKeywords: ['broken image', 'missing image', 'image not loading', 'photo not showing', 'blank image', 'image error', 'upload failed', 'image disappeared', 'republish images', 'clear cache', 'hard refresh', 'image size', 'file too large', 'image upload issue'],
          intentPhrases: ['why aren\'t my images showing', 'my photos are missing on my site', 'how to fix broken images', 'images disappeared after publishing', 'why is my image not loading', 'image upload not working'],
          tags: ['troubleshooting', 'images', 'publishing'],
        },
        paragraphs: [
          'If images appear broken or missing on your published site, the most common cause is a cached version of the page. Try opening your site in a private or incognito window, or do a hard refresh with Cmd+Shift+R (Mac) or Ctrl+Shift+F5 (Windows).',
          'If you recently uploaded a new image and it\'s not appearing, go back to the Site Editor and confirm it shows in the preview panel. If it does, click Publish again to push the updated version live.',
          'Images uploaded from your device are processed and stored on our servers. If the upload was interrupted — due to a slow connection, a tab closing, or a page refresh — the image may not have saved correctly. Try re-uploading and republishing.',
          'Very large image files (over 10MB) can fail silently during upload. Resize your image to under 5MB before uploading. Free tools like Squoosh.app or the Preview app on Mac can reduce file size without visible quality loss.',
        ],
      },
      {
        id: 'stripe-connection-issues',
        title: "I can't connect my Stripe account",
        audience: 'practitioner',
        keywords: ['stripe', 'connect', 'payment setup', 'verification', 'bank account', 'stripe error'],
        searchMetadata: {
          aiKeywords: ['Stripe error', 'Stripe not connecting', 'payment setup failed', 'Stripe verification', 'identity verification', 'bank account issue', 'Stripe stuck', 'can\'t connect Stripe', 'payment not working', 'Stripe onboarding', 'SSN verification', 'Stripe account problem'],
          intentPhrases: ['why can\'t I connect Stripe', 'Stripe connection is failing', 'how to fix Stripe setup', 'Stripe verification not completing', 'my Stripe won\'t connect', 'I\'m stuck on Stripe setup'],
          tags: ['troubleshooting', 'stripe', 'payments'],
        },
        paragraphs: [
          'If you\'re having trouble connecting Stripe, the most common reason is that Stripe\'s verification isn\'t yet complete. Go to Settings > Business & Payments and click "Connect with Stripe" again — Stripe will show you exactly what information is still needed.',
          'Stripe requires identity verification to enable payouts. This typically includes your legal name, date of birth, the last 4 digits of your Social Security Number (US), and a bank account for deposits. If any of these were entered incorrectly, Stripe will flag them and prompt you to resubmit.',
          'If you already have a Stripe account under a different email address, you can connect it during setup by signing in to that existing account when prompted — you don\'t need to create a new one.',
          'If the issue persists after completing Stripe\'s verification steps, contact us at support@divineframeworks.com with your account email. We\'ll help resolve it directly.',
        ],
      },
      {
        id: 'calendar-not-syncing',
        title: "My calendar isn't syncing",
        audience: 'practitioner',
        keywords: ['calendar', 'google calendar', 'sync', 'not syncing', 'missing bookings', 'timezone'],
        searchMetadata: {
          aiKeywords: ['calendar not working', 'sync broken', 'bookings not appearing', 'Google Calendar issue', 'calendar disconnected', 'wrong time', 'timezone mismatch', 'calendar error', 'reconnect calendar', 'sync failed', 'missing appointments', 'calendar not updating'],
          intentPhrases: ['why isn\'t my calendar syncing', 'bookings not showing on my calendar', 'how to fix calendar sync', 'my calendar shows wrong times', 'calendar stopped working', 'how to reconnect Google Calendar'],
          tags: ['troubleshooting', 'calendar', 'integrations'],
        },
        paragraphs: [
          'If new bookings aren\'t appearing on your Google Calendar, start by checking that the connection is still active. Go to Settings > Availability & Scheduling — a green "Connected" status means everything is linked. If you see a disconnected state, reconnect it.',
          'Google sometimes revokes calendar access after a password change or extended inactivity. If this happens, disconnect and reconnect your calendar from the Availability settings page. The process takes about 30 seconds.',
          'A timezone mismatch is another common culprit. If bookings are appearing at the wrong times, confirm that your timezone in Divine Frameworks matches the timezone set on your Google Calendar. Check your Availability settings and adjust if needed.',
          'Calendar sync only applies to new bookings made after you connected. Existing bookings won\'t be retroactively added to Google Calendar — only future ones sync automatically.',
        ],
      },
    ],
  },

  {
    id: 'connecting-your-website',
    slug: 'connecting-your-website',
    title: 'Connecting Your Website',
    description: 'Add a booking button to your existing site',
    Icon: Globe,
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
    audience: 'practitioner',
    articles: [
      {
        id: 'adding-booking-button',
        title: 'Adding the booking button to your website',
        audience: 'practitioner',
        keywords: ['widget', 'booking button', 'embed', 'code snippet', 'existing website', 'add to site', 'connect website'],
        searchMetadata: {
          aiKeywords: ['booking widget', 'embed code', 'script tag', 'booking link', 'add button', 'external website', 'own website', 'existing site', 'code snippet', 'copy code', 'paste code', 'install widget', 'connect website', 'booking button setup'],
          intentPhrases: ['how do I add a booking button to my website', 'how to embed the booking widget', 'where do I get the code snippet', 'how to connect my existing website', 'how to add DF bookings to my site'],
          tags: ['widget', 'existing-website', 'booking'],
        },
        paragraphs: [
          'If you have an existing website, you can add a Divine Frameworks booking button so clients can book directly from your site.',
          'Go to Settings \u2192 Connect Your Website in your dashboard. Choose your button text, color, and shape, then copy the code snippet.',
          'Paste the snippet into your website wherever you want the button to appear \u2014 usually on your homepage, services page, or contact page.',
          'When a client clicks the button, they\'re taken to your Divine Frameworks profile where they can browse your services and book a session.',
          'Need help adding it to a specific platform? We have step-by-step guides for Squarespace, Wix, WordPress, and Shopify below.',
        ],
      },
      {
        id: 'squarespace-booking-button',
        title: 'Squarespace \u2014 Adding the booking button',
        audience: 'practitioner',
        keywords: ['squarespace', 'add button squarespace', 'code injection', 'code block', 'embed squarespace'],
        searchMetadata: {
          aiKeywords: ['squarespace widget', 'squarespace embed', 'squarespace code block', 'squarespace code injection', 'add script squarespace', 'squarespace booking', 'squarespace button', 'squarespace custom code'],
          intentPhrases: ['how to add booking button to squarespace', 'squarespace code injection', 'embed code on squarespace', 'add widget to squarespace site'],
          tags: ['widget', 'squarespace', 'platform-guide'],
        },
        paragraphs: [
          'To add your Divine Frameworks booking button to a Squarespace website:',
          '1. Log in to your Squarespace site and open the page editor.\n2. Click "Add Block" where you want the button to appear.\n3. Select "Code" from the block options.\n4. Paste your booking button code snippet (from Settings \u2192 Connect Your Website in your DF dashboard).\n5. Click "Apply" to save the code block.\n6. Save and publish your page.',
          'The button will appear wherever you placed the code block. You can move it by dragging the block to a different position on the page.',
          'Alternatively, to add the button to every page: Go to Settings \u2192 Advanced \u2192 Code Injection \u2192 Footer, and paste the snippet there. The button will appear at the bottom of every page.',
        ],
      },
      {
        id: 'wix-booking-button',
        title: 'Wix \u2014 Adding the booking button',
        audience: 'practitioner',
        keywords: ['wix', 'add button wix', 'custom code', 'html embed', 'embed wix'],
        searchMetadata: {
          aiKeywords: ['wix widget', 'wix embed', 'wix custom code', 'wix html embed', 'add script wix', 'wix booking', 'wix button', 'wix editor x', 'wix studio'],
          intentPhrases: ['how to add booking button to wix', 'wix embed code', 'add widget to wix site', 'wix custom code embed'],
          tags: ['widget', 'wix', 'platform-guide'],
        },
        paragraphs: [
          'To add your Divine Frameworks booking button to a Wix website:',
          '1. Open your Wix site editor.\n2. Click "Add" (+) on the left sidebar.\n3. Select "Embed Code" \u2192 "Custom Code."\n4. Paste your booking button code snippet (from Settings \u2192 Connect Your Website in your DF dashboard).\n5. Position the code element where you want the button to appear.\n6. Click "Publish" to save your changes.',
          'If you\'re using Wix\'s new editor (Editor X / Wix Studio), the steps are similar \u2014 look for the "Custom Code" or "HTML Embed" option in the add panel.',
        ],
      },
      {
        id: 'wordpress-booking-button',
        title: 'WordPress \u2014 Adding the booking button',
        audience: 'practitioner',
        keywords: ['wordpress', 'add button wordpress', 'custom html', 'gutenberg', 'widget', 'plugin', 'header footer'],
        searchMetadata: {
          aiKeywords: ['wordpress widget', 'wordpress embed', 'wordpress custom html', 'gutenberg block', 'wordpress plugin', 'insert headers footers', 'wpcode', 'wordpress sidebar', 'wordpress footer', 'wordpress booking button', 'wordpress script'],
          intentPhrases: ['how to add booking button to wordpress', 'wordpress custom html block', 'add widget to wordpress site', 'wordpress embed script tag'],
          tags: ['widget', 'wordpress', 'platform-guide'],
        },
        paragraphs: [
          'To add your Divine Frameworks booking button to a WordPress website:',
          'Option 1 \u2014 On a specific page:\n1. Open the page or post where you want the button.\n2. Add a "Custom HTML" block (in the Gutenberg editor, click + and search for "Custom HTML").\n3. Paste your booking button code snippet.\n4. Update or publish the page.',
          'Option 2 \u2014 In a sidebar or footer widget:\n1. Go to Appearance \u2192 Widgets.\n2. Add a "Custom HTML" widget to your desired widget area.\n3. Paste the code snippet.\n4. Save.',
          'Option 3 \u2014 On every page (via header/footer plugin):\n1. Install a plugin like "Insert Headers and Footers" or "WPCode."\n2. Go to the plugin settings.\n3. Paste the snippet in the footer section.\n4. Save \u2014 the button appears on every page.',
        ],
      },
      {
        id: 'shopify-booking-button',
        title: 'Shopify \u2014 Adding the booking button',
        audience: 'practitioner',
        keywords: ['shopify', 'add button shopify', 'custom liquid', 'theme editor', 'embed shopify'],
        searchMetadata: {
          aiKeywords: ['shopify widget', 'shopify embed', 'shopify custom liquid', 'shopify theme editor', 'shopify code', 'shopify booking button', 'shopify script', 'shopify sections', 'shopify footer'],
          intentPhrases: ['how to add booking button to shopify', 'shopify custom code section', 'add widget to shopify store', 'shopify embed script'],
          tags: ['widget', 'shopify', 'platform-guide'],
        },
        paragraphs: [
          'To add your Divine Frameworks booking button to a Shopify store:',
          '1. Go to your Shopify admin \u2192 Online Store \u2192 Themes.\n2. Click "Customize" on your active theme.\n3. Navigate to the page where you want the button.\n4. Add a "Custom Liquid" or "Custom HTML" section.\n5. Paste your booking button code snippet.\n6. Save.',
          'For the booking button to appear on all pages, you can add it to your theme\'s layout file: Go to Online Store \u2192 Themes \u2192 Edit Code \u2192 Sections, and add the snippet to your footer or a custom section.',
        ],
      },
      {
        id: 'other-platforms-booking-button',
        title: 'Other platforms \u2014 Adding the booking button',
        audience: 'practitioner',
        keywords: ['other platform', 'general', 'any website', 'custom code', 'html embed', 'code injection', 'link', 'direct link'],
        searchMetadata: {
          aiKeywords: ['other website builder', 'generic embed', 'any platform', 'custom html', 'code injection', 'embed anywhere', 'direct link', 'simple link', 'no code', 'html embed generic', 'weebly', 'ghost', 'webflow', 'godaddy'],
          intentPhrases: ['how to add booking button to any website', 'my platform is not listed', 'general widget instructions', 'can I use a link instead of code', 'how to add booking to webflow'],
          tags: ['widget', 'general', 'platform-guide'],
        },
        paragraphs: [
          'The Divine Frameworks booking button works on any website that supports custom HTML or JavaScript.',
          'General steps:\n1. Find the "Custom Code," "HTML Embed," or "Code Injection" option in your website builder.\n2. Paste your booking button code snippet (from Settings \u2192 Connect Your Website in your DF dashboard).\n3. Save and publish.',
          'If your platform doesn\'t support custom code, you can use the direct link instead: copy your DF profile link and add it as a regular link or button on your site. Your profile link is always available in Settings \u2192 Connect Your Website.',
          'If you\'re having trouble, contact us at support@divineframeworks.com and we\'ll help you get set up.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Seeker categories (audience: 'seeker')
  // ---------------------------------------------------------------------------

  {
    id: 'getting-set-up-seeker',
    slug: 'getting-set-up-seeker',
    title: 'Getting Set Up',
    description: 'Create your account and find the right practitioner',
    Icon: Rocket,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    audience: 'seeker',
    articles: [
      {
        id: 'how-divine-frameworks-works',
        title: 'How Divine Frameworks works',
        audience: 'seeker',
        keywords: ['how it works', 'what is divine frameworks', 'browse practitioners', 'book session', 'find practitioner'],
        searchMetadata: {
          aiKeywords: ['what is DF', 'how does it work', 'platform overview', 'find healer', 'find reader', 'spiritual services', 'wellness platform', 'browse healers', 'book online', 'guest booking', 'no account needed', 'practitioner marketplace', 'holistic services', 'spiritual directory'],
          intentPhrases: ['what is Divine Frameworks', 'how does this work', 'how do I find a practitioner', 'do I need an account to book', 'what can I do here', 'how do I use this site'],
          tags: ['overview', 'getting-started', 'platform'],
        },
        paragraphs: [
          'Divine Frameworks connects you with trusted spiritual and wellness practitioners — tarot readers, energy healers, astrologers, life coaches, mediums, and more. You can browse practitioners, read reviews, and book sessions directly through the platform.',
          'Here\'s how it works: Browse the practitioner directory to find someone who resonates with you. Each practitioner has a profile showing their services, pricing, availability, and reviews from other clients. When you\'re ready, book a session and pay securely online. You\'ll receive a confirmation email with all the details, including any links for virtual sessions.',
          'You don\'t need an account to book a session — you can book as a guest using just your name and email. But creating a free account gives you a central place to manage all your sessions, save your favorite practitioners, and keep a record of your journey.',
        ],
      },
      {
        id: 'creating-your-seeker-account',
        title: 'Creating your account',
        audience: 'seeker',
        keywords: ['sign up', 'create account', 'register', 'free account', 'join', 'get started'],
        searchMetadata: {
          aiKeywords: ['signup', 'sign-up', 'register', 'join', 'free account', 'new account', 'Google signup', 'email signup', 'session hub', 'create profile', 'get started', 'log in first time', 'account creation'],
          intentPhrases: ['how do I create an account', 'how to sign up', 'is it free to join', 'do I need an account', 'how to register', 'can I sign up with Google'],
          tags: ['account', 'getting-started', 'signup'],
        },
        paragraphs: [
          'Creating a Divine Frameworks account is free and takes less than a minute. You can sign up with your email address or use your Google account.',
          'Here\'s how: Visit divineframeworks.com and click "Sign Up." You\'ll be asked what brings you to Divine Frameworks — select "I\'m looking for a practitioner." Enter your name, email, and create a password. That\'s it — you\'ll be taken to your session hub where you can start browsing practitioners.',
          'Your account gives you: a session hub to manage all your bookings, the ability to save favorite practitioners, message history, and a record of your past sessions with any notes your practitioners share with you.',
        ],
      },
      {
        id: 'setting-up-seeker-profile',
        title: 'Setting up your profile',
        audience: 'seeker',
        keywords: ['profile', 'account settings', 'update name', 'edit profile', 'personal information'],
        searchMetadata: {
          aiKeywords: ['edit profile', 'profile settings', 'change name', 'update info', 'personal details', 'display name', 'profile privacy', 'who can see my info', 'account info', 'user profile'],
          intentPhrases: ['how do I update my profile', 'where are my profile settings', 'can practitioners see my information', 'how to change my name', 'is my profile public'],
          tags: ['profile', 'settings', 'privacy'],
        },
        paragraphs: [
          'Your profile helps practitioners know who you are when you book a session. You can keep it as simple or detailed as you\'d like.',
          'To update your profile, click your name in the top-right corner and select "Account settings." From there you can update your display name, email, and password.',
          'Your profile information is only visible to practitioners you book with — it\'s never displayed publicly on the platform.',
        ],
      },
      {
        id: 'what-to-expect-new-user',
        title: 'What to expect as a new user',
        audience: 'seeker',
        keywords: ['new user', 'first time', 'what can I do', 'overview', 'features', 'how to use'],
        searchMetadata: {
          aiKeywords: ['first time user', 'beginner guide', 'getting started', 'new to DF', 'what can I do', 'feature overview', 'browse', 'book', 'favorites', 'session hub', 'how to use', 'quick start', 'platform tour'],
          intentPhrases: ['what can I do on Divine Frameworks', 'I\'m new here what should I do', 'how do I get started as a seeker', 'what features do I have', 'where do I start'],
          tags: ['overview', 'getting-started', 'features'],
        },
        paragraphs: [
          'Welcome! Here\'s a quick overview of what you can do on Divine Frameworks.',
          'Browse practitioners: Use the directory to find practitioners by service type, location, price range, or simply describe what you\'re looking for and let our AI guide help match you.',
          'Book a session: Once you find a practitioner, pick a service, choose an available time, and pay securely. You\'ll receive a confirmation email right away.',
          'Manage your sessions: From your session hub, you can view upcoming and past sessions, reschedule or cancel if needed, and re-book with practitioners you love.',
          'Save favorites: Found a practitioner you\'d like to come back to? Click the heart icon to save them to your Favorites list.',
          'Everything on Divine Frameworks is designed to make finding and booking with practitioners as easy and comfortable as possible.',
        ],
      },
    ],
  },

  {
    id: 'finding-booking',
    slug: 'finding-booking',
    title: 'Finding & Booking',
    description: 'Search the directory, compare practitioners, and book sessions',
    Icon: Search,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    audience: 'seeker',
    articles: [
      {
        id: 'browsing-practitioner-directory',
        title: 'Browsing the practitioner directory',
        audience: 'seeker',
        keywords: ['directory', 'browse', 'search', 'find practitioners', 'filter', 'sort', 'AI seeker guide', 'discover'],
        searchMetadata: {
          aiKeywords: ['find a practitioner', 'search directory', 'browse healers', 'filter practitioners', 'sort by price', 'sort by rating', 'AI matching', 'seeker guide', 'practitioner search', 'discover practitioners', 'service type filter', 'location filter', 'virtual practitioners', 'in person sessions'],
          intentPhrases: ['how do I find a practitioner', 'how to search the directory', 'how to filter practitioners', 'how does the AI guide work', 'how to browse practitioners', 'can I search by location'],
          tags: ['directory', 'search', 'discovery'],
        },
        paragraphs: [
          'The practitioner directory is where you\'ll find all the practitioners on Divine Frameworks. You can get there by clicking "Browse" in your navigation bar.',
          'You can search by name, service type, or location. Use the filters to narrow results by service type, session format (virtual or in-person), and price range. You can also sort by rating, price, or featured practitioners.',
          'If you\'re not sure what you\'re looking for, try the AI Seeker Guide — describe what you need in your own words ("I\'m going through a career transition and need clarity") and our AI will match you with practitioners who can help.',
          'Each practitioner card shows their name, services, ratings, price range, and availability. Click "View Profile" to see their full page with detailed service descriptions, reviews, and booking options.',
        ],
      },
      {
        id: 'understanding-services-pricing',
        title: 'Understanding services and pricing',
        audience: 'seeker',
        keywords: ['pricing', 'cost', 'how much', 'services', 'session types', 'rates', 'fees', 'price range', 'affordable'],
        searchMetadata: {
          aiKeywords: ['session cost', 'how much does a session cost', 'price range', 'service types', 'tarot price', 'reiki cost', 'coaching rate', 'affordable sessions', 'session duration', 'virtual vs in person', 'session format', 'cheap sessions', 'expensive', 'budget', 'value'],
          intentPhrases: ['how much do sessions cost', 'what are typical prices', 'how do I choose a service', 'what types of sessions are available', 'are there affordable options', 'what\'s the difference between services'],
          tags: ['pricing', 'services', 'booking'],
        },
        paragraphs: [
          'Each practitioner sets their own services and prices. On their profile, you\'ll see a list of services they offer with the name, description, duration, and price for each.',
          'Common service types include tarot readings, energy healing, astrology consultations, mediumship sessions, life coaching, and more. Prices typically range from $50 to $200+ per session depending on the type, duration, and the practitioner\'s experience.',
          'Session formats may be virtual (via Zoom or similar) or in-person. This is noted on each service listing so you know what to expect before booking.',
          'If a practitioner offers multiple services at different price points, take a moment to read the descriptions — the right service depends on what you\'re looking for, not just the price.',
        ],
      },
      {
        id: 'how-to-book-session',
        title: 'How to book a session',
        audience: 'seeker',
        keywords: ['book', 'booking', 'schedule', 'appointment', 'reserve', 'how to book', 'make appointment'],
        searchMetadata: {
          aiKeywords: ['make a booking', 'schedule appointment', 'reserve session', 'booking steps', 'book now', 'pick a time', 'choose time slot', 'pay for session', 'checkout', 'booking process', 'guest booking', 'book without account', 'confirm booking'],
          intentPhrases: ['how do I book a session', 'how to make an appointment', 'how to schedule a session', 'can I book without an account', 'what are the steps to book', 'how do I pick a time'],
          tags: ['booking', 'how-to', 'sessions'],
        },
        paragraphs: [
          'Booking a session takes just a few steps:',
          '1. Find a practitioner you\'d like to work with from the directory or your favorites list. 2. On their profile, choose the service you\'d like to book. 3. Select a date and time from their available slots. 4. Enter your name and email (or log in to your account). 5. Complete your payment securely through Stripe.',
          'After booking, you\'ll receive a confirmation email with all the details — date, time, service name, and any links for virtual sessions. If the practitioner has an intake form, you may also receive a questionnaire to fill out before your session.',
          'You can book without creating an account, but having one makes it easy to manage all your sessions in one place.',
        ],
      },
      {
        id: 'what-happens-after-booking',
        title: 'What happens after you book',
        audience: 'seeker',
        keywords: ['after booking', 'confirmation', 'what next', 'email', 'reminder', 'intake form', 'prepare', 'video link'],
        searchMetadata: {
          aiKeywords: ['booking confirmation', 'confirmation email', 'what next', 'session preparation', 'intake questionnaire', 'session reminder', 'Zoom link', 'video link', 'manage booking link', 'before my session', 'day of session', 'session details', 'meeting link'],
          intentPhrases: ['what happens after I book', 'where is my confirmation email', 'how do I prepare for my session', 'where is the video link', 'will I get a reminder', 'what is an intake form'],
          tags: ['booking', 'confirmation', 'preparation'],
        },
        paragraphs: [
          'After you complete your booking, here\'s what to expect:',
          'Confirmation email: You\'ll receive an email right away with your session details, including the date, time, service, and practitioner name. If it\'s a virtual session, the video link will be included.',
          'Manage your booking: The confirmation email includes a "Manage your booking" link that lets you reschedule or cancel if needed — no login required.',
          'Intake form (if applicable): Some practitioners send an intake questionnaire before your first session. Check your email for this — completing it helps your practitioner prepare for your session.',
          'Session reminder: You\'ll receive a reminder email 24 hours before your session with all the details.',
          'On the day: For virtual sessions, click the video link in your confirmation or reminder email at your scheduled time. For in-person sessions, the practitioner\'s address or meeting location will be in your booking details.',
        ],
      },
    ],
  },

  {
    id: 'managing-sessions',
    slug: 'managing-sessions',
    title: 'Managing Your Sessions',
    description: 'View, reschedule, or cancel your upcoming sessions',
    Icon: Calendar,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    audience: 'seeker',
    articles: [
      {
        id: 'your-session-hub',
        title: 'Your session hub',
        audience: 'seeker',
        keywords: ['session hub', 'my sessions', 'manage bookings', 'view sessions', 'upcoming', 'past sessions', 'history'],
        searchMetadata: {
          aiKeywords: ['my sessions page', 'session dashboard', 'booking manager', 'upcoming sessions', 'past sessions', 'session history', 'sessions tab', 'messages tab', 'favorites tab', 'filter sessions', 'session recordings', 'practitioner notes', 'session list'],
          intentPhrases: ['where are my sessions', 'how to see my bookings', 'how to find past sessions', 'where is my session hub', 'how to manage my bookings', 'can I see session notes'],
          tags: ['sessions', 'dashboard', 'management'],
        },
        paragraphs: [
          'Your session hub is your home base on Divine Frameworks. It\'s where you can see all your upcoming and past sessions in one place.',
          'To access it, click "My Sessions" in the navigation bar (you\'ll need to be logged in).',
          'The Sessions tab shows your upcoming sessions at the top with options to reschedule, cancel, or book again. Below that, your past sessions appear with any notes your practitioner has shared and links to session recordings (if available).',
          'The Messages tab shows practitioners you\'ve communicated with, and the Favorites tab shows practitioners you\'ve saved from the directory.',
          'You can filter your past sessions by service type or practitioner to find specific sessions quickly.',
        ],
      },
      {
        id: 'rescheduling-session',
        title: 'Rescheduling a session',
        audience: 'seeker',
        keywords: ['reschedule', 'change time', 'move appointment', 'new time', 'switch session', 'reschedule free'],
        searchMetadata: {
          aiKeywords: ['change session time', 'move appointment', 'new time slot', 'free reschedule', 'reschedule from email', 'manage booking link', 'pick new time', 'change service', 'price adjustment', 'reschedule policy', 'reschedule limit'],
          intentPhrases: ['how do I reschedule', 'can I change my session time', 'is rescheduling free', 'how to move my appointment', 'how to reschedule from email', 'what if the new service costs more'],
          tags: ['reschedule', 'sessions', 'booking-management'],
        },
        paragraphs: [
          'Need to change your session time? No problem. You get one free reschedule per booking.',
          'From your session hub: Click "Reschedule" on any upcoming session. You\'ll see the practitioner\'s available times — pick a new one, review the change, and confirm.',
          'From email: Your booking confirmation and reminder emails include a "Manage your booking" link. Click it and select "Reschedule." No login needed.',
          'If you\'re switching to a different service at a different price, any price difference will be handled automatically — you\'ll either be charged the difference or refunded.',
          'After your first free reschedule, additional reschedules may be subject to the practitioner\'s cancellation policy.',
        ],
      },
      {
        id: 'cancelling-session',
        title: 'Cancelling a session',
        audience: 'seeker',
        keywords: ['cancel', 'cancellation', 'refund', 'cancel booking', 'cancel appointment', 'get money back', 'cancellation policy'],
        searchMetadata: {
          aiKeywords: ['cancel session', 'cancel appointment', 'get refund', 'money back', 'cancellation fee', 'cancel from email', 'cancel from hub', 'practitioner cancelled', 'full refund', 'partial refund', 'no refund', 'cancellation window', 'cancel policy'],
          intentPhrases: ['how do I cancel a session', 'will I get a refund', 'how to cancel from email', 'what if the practitioner cancels', 'how much refund will I get', 'what is the cancellation policy'],
          tags: ['cancellation', 'refunds', 'sessions'],
        },
        paragraphs: [
          'You can cancel a session at any time before the scheduled date, but your refund depends on the practitioner\'s cancellation policy and when you cancel.',
          'From your session hub: Click "Cancel" on any upcoming session. You\'ll see exactly how much you\'ll be refunded before you confirm.',
          'From email: Use the "Manage your booking" link in your confirmation or reminder email to cancel. No login needed.',
          'How refunds work: Each practitioner sets their own cancellation policy (for example, "full refund if cancelled more than 24 hours before the session"). If you cancel within the policy window, you may receive a partial refund or no refund. The exact amount is always shown before you confirm the cancellation.',
          'If a practitioner cancels your session, you always receive a full refund — no exceptions.',
        ],
      },
      {
        id: 'practitioner-proposes-reschedule',
        title: 'When a practitioner proposes a reschedule',
        audience: 'seeker',
        keywords: ['practitioner reschedule', 'proposal', 'proposed time', 'schedule change', 'accept decline', 'counter propose'],
        searchMetadata: {
          aiKeywords: ['practitioner changed time', 'reschedule proposal', 'accept new time', 'decline proposal', 'suggest different time', 'counter proposal', 'schedule change notification', 'response deadline', 'practitioner initiated change', 'full refund on practitioner cancel'],
          intentPhrases: ['my practitioner wants to reschedule', 'how do I respond to a reschedule proposal', 'can I suggest a different time', 'what if I can\'t make the new time', 'do I get a refund if they reschedule', 'what happens if I don\'t respond'],
          tags: ['reschedule', 'proposals', 'sessions'],
        },
        paragraphs: [
          'Sometimes a practitioner may need to reschedule your session. If this happens, you\'ll receive an email with their proposed new time and an optional message explaining why.',
          'You have three options:',
          'Accept the new time: Your session moves to the proposed time. Both you and the practitioner are notified.',
          'Suggest a different time: If the proposed time doesn\'t work for you, you can suggest an alternative from the practitioner\'s available slots. They\'ll review and accept or let you know if it doesn\'t work.',
          'Cancel the session: If no available time works for you, you can cancel with a full refund — since the practitioner initiated the change, you\'re never penalized.',
          'You\'ll have a deadline to respond (shown in the email and on the response page). If you don\'t respond in time, your original session time stays as-is.',
        ],
      },
    ],
  },

  {
    id: 'payments-refunds',
    slug: 'payments-refunds',
    title: 'Payments & Refunds',
    description: 'Understand charges, receipts, and refund policies',
    Icon: CreditCard,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    audience: 'seeker',
    articles: [
      {
        id: 'how-payments-work',
        title: 'How payments work',
        audience: 'seeker',
        keywords: ['payment', 'pay', 'credit card', 'debit card', 'secure', 'Stripe', 'how to pay', 'payment method', 'billing'],
        searchMetadata: {
          aiKeywords: ['pay for session', 'credit card payment', 'debit card', 'Stripe checkout', 'secure payment', 'payment processing', 'card details', 'payment confirmation', 'receipt', 'payment history', 'how to pay', 'accepted cards', 'payment at booking'],
          intentPhrases: ['how do I pay for a session', 'what payment methods are accepted', 'is payment secure', 'does DF take a cut of my payment', 'where can I see my payment history', 'when am I charged'],
          tags: ['payments', 'stripe', 'security'],
        },
        paragraphs: [
          'All payments on Divine Frameworks are processed securely through Stripe, one of the world\'s most trusted payment platforms.',
          'When you book a session, you pay the full amount at the time of booking using a credit or debit card. Your payment goes directly to the practitioner — Divine Frameworks does not take any percentage of your payment.',
          'Stripe handles all payment security, including PCI compliance and encryption. Your card details are never stored on Divine Frameworks servers.',
          'After payment, you\'ll receive a confirmation email with your booking details. Your payment history is visible in your session hub under past sessions.',
        ],
      },
      {
        id: 'refund-policies',
        title: 'Refund policies',
        audience: 'seeker',
        keywords: ['refund', 'money back', 'cancellation refund', 'refund policy', 'how long refund', 'partial refund', 'full refund'],
        searchMetadata: {
          aiKeywords: ['get money back', 'refund timeline', 'refund amount', 'cancellation refund', 'practitioner cancelled refund', 'partial refund', 'no refund', 'refund to card', 'refund waiting time', 'bank processing', '5-10 business days', 'cancellation window', 'refund rules'],
          intentPhrases: ['how do refunds work', 'when will I get my refund', 'how long does a refund take', 'do I get a full refund', 'what if the practitioner cancels', 'where is my refund', 'can I get my money back'],
          tags: ['refunds', 'cancellation', 'payments'],
        },
        paragraphs: [
          'Refunds on Divine Frameworks depend on who cancelled and when:',
          'You cancel outside the policy window: You receive a refund based on the practitioner\'s policy (often a full refund if cancelled more than 24-48 hours before the session).',
          'You cancel inside the policy window: The practitioner\'s policy applies — this may mean a partial refund or no refund. The exact amount is always shown before you confirm.',
          'The practitioner cancels: You always receive a full refund, no exceptions. This is a platform-wide rule.',
          'A practitioner proposes a reschedule and you can\'t make it: If you cancel because the practitioner changed the time, you receive a full refund.',
          'Refunds typically appear on your original payment method within 5-10 business days, depending on your bank.',
          'Each practitioner\'s cancellation policy is visible on their profile before you book, so you always know what to expect.',
        ],
      },
      {
        id: 'payment-security',
        title: 'Payment security',
        audience: 'seeker',
        keywords: ['security', 'safe', 'secure payment', 'PCI', 'credit card safety', 'fraud', 'unauthorized charge', 'data protection'],
        searchMetadata: {
          aiKeywords: ['is it safe', 'payment safety', 'card security', 'PCI compliant', 'data encryption', 'fraud protection', 'unauthorized charge', 'stolen card', 'identity theft', 'secure checkout', 'Stripe security', 'card stored', 'data privacy'],
          intentPhrases: ['is my payment safe', 'is my credit card secure', 'does DF store my card', 'what if I see an unauthorized charge', 'how secure is the payment', 'is Stripe safe to use'],
          tags: ['security', 'payments', 'privacy'],
        },
        paragraphs: [
          'Your payment information is always safe on Divine Frameworks.',
          'We use Stripe, the same payment processor trusted by millions of businesses worldwide, including companies like Shopify, Amazon, and Lyft. Stripe is PCI Level 1 compliant — the highest level of payment security certification.',
          'Your credit card details are never stored on our servers. They go directly to Stripe\'s secure infrastructure.',
          'If you ever see an unauthorized charge related to a Divine Frameworks booking, contact us at support@divineframeworks.com and we\'ll investigate immediately.',
        ],
      },
    ],
  },

  {
    id: 'becoming-practitioner',
    slug: 'becoming-practitioner',
    title: 'Becoming a Practitioner',
    description: 'Switch from seeker to practitioner and start your practice',
    Icon: ArrowUpCircle,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    audience: 'seeker',
    articles: [
      {
        id: 'from-seeker-to-practitioner',
        title: 'From seeker to practitioner',
        audience: 'seeker',
        keywords: ['become practitioner', 'start practice', 'offer services', 'switch to practitioner', 'seeker to practitioner', 'upgrade'],
        searchMetadata: {
          aiKeywords: ['switch roles', 'become a practitioner', 'start offering services', 'practitioner account', 'seeker upgrade', 'offer readings', 'offer healing', 'start my practice', 'practitioner setup', 'keep seeker data', 'role switch', 'practitioner onboarding'],
          intentPhrases: ['how do I become a practitioner', 'can I switch from seeker to practitioner', 'do I lose my seeker data', 'how to start offering services', 'where is the become practitioner button', 'what happens to my favorites'],
          tags: ['practitioner', 'role-switch', 'getting-started'],
        },
        paragraphs: [
          'If you\'re thinking about offering your own spiritual or wellness services, Divine Frameworks makes it easy to get started — and you don\'t have to give up your seeker account to do it.',
          'To become a practitioner, click your profile icon in the top-right corner and select "Become a practitioner." You\'ll be guided through a quick setup process to get your practice up and running.',
          'As a practitioner, you\'ll get access to tools for building a professional website, managing bookings and payments, communicating with clients, and growing your practice.',
          'Your existing session history and favorites from your seeker account are preserved — you don\'t lose anything.',
        ],
      },
      {
        id: 'what-practitioners-can-do',
        title: 'What you can do as a practitioner',
        audience: 'seeker',
        keywords: ['practitioner features', 'what can I do', 'tools', 'website', 'bookings', 'payments', 'pricing', 'plans', 'subscription'],
        searchMetadata: {
          aiKeywords: ['practitioner tools', 'website builder', 'accept bookings', 'accept payments', 'client management', 'session notes', 'Google Calendar', 'Business Playbook', 'AI tools', 'practice management', 'practitioner dashboard', 'keep 100% revenue', 'no commission'],
          intentPhrases: ['what tools do practitioners get', 'can I build a website', 'do I keep all my earnings', 'what features come with being a practitioner', 'how do practitioners accept payments', 'is there a website builder'],
          tags: ['practitioner', 'features', 'tools'],
        },
        paragraphs: [
          'As a Divine Frameworks practitioner, you have everything you need to run a professional practice:',
          'Build a beautiful website: Create a customized site with your services, bio, testimonials, gallery, and booking calendar — no coding or design experience needed.',
          'Accept bookings and payments: Clients book directly through your site and pay securely. You keep 100% of your booking revenue — Divine Frameworks charges a monthly subscription, not a cut of your earnings.',
          'Manage your practice: Track clients, write session notes, manage your schedule, sync with Google Calendar, and communicate with clients through the platform.',
          'Grow with guidance: Access the Business Playbook for practical business education, and use AI-powered tools to prepare for sessions and gain practice insights.',
        ],
      },
      {
        id: 'practitioner-pricing-plans',
        title: 'Practitioner pricing plans',
        audience: 'seeker',
        keywords: ['pricing', 'plans', 'cost', 'subscription', 'free plan', 'how much', 'monthly fee', 'tiers', 'compare plans'],
        searchMetadata: {
          aiKeywords: ['practitioner cost', 'monthly subscription', 'essentials plan', 'booking and directory plan', 'professional plan', 'premier plan', 'free tier', '$29', '$49', '$99', 'Stripe fee', 'processing fee', 'plan comparison', 'upgrade', 'downgrade', 'which plan'],
          intentPhrases: ['how much does it cost to be a practitioner', 'what are the practitioner plans', 'is there a free plan', 'what does each plan include', 'what is the Stripe fee', 'can I change plans later'],
          tags: ['pricing', 'subscription', 'plans'],
        },
        paragraphs: [
          'Divine Frameworks offers flexible plans for practitioners at every stage:',
          'Essentials (Free): Get listed in the directory with a basic profile. Includes up to 3 bookings per month. Great for testing the waters.',
          'Booking & Directory ($29/month): Unlimited bookings, payment processing, client management, Google Calendar sync, and directory listing. Everything you need to run your practice without building a website.',
          'Professional ($49/month): Everything above plus the DF website builder with all templates, customization options, and a verification badge.',
          'Premier ($99/month): Everything above plus AI-powered session prep, practice coaching insights, and priority placement in the directory.',
          'All paid plans let you keep 100% of your booking revenue. The only other cost is Stripe\'s standard processing fee (2.9% + $0.30 per transaction), which is the same fee charged by all major payment processors.',
          'You can start with any plan and upgrade or downgrade at any time.',
        ],
      },
    ],
  },

  {
    id: 'account-security-seeker',
    slug: 'account-security-seeker',
    title: 'Account & Security',
    description: 'Manage your account, password, and privacy settings',
    Icon: Shield,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    audience: 'seeker',
    articles: [
      {
        id: 'updating-your-information',
        title: 'Updating your information',
        audience: 'seeker',
        keywords: ['update account', 'change name', 'change email', 'edit information', 'account settings', 'update profile'],
        searchMetadata: {
          aiKeywords: ['edit account', 'change display name', 'update email address', 'change personal info', 'modify account', 'account details', 'name change', 'email change', 'profile update', 'settings page'],
          intentPhrases: ['how do I change my name', 'how to update my email', 'where are my account settings', 'how to edit my information', 'will my old bookings update'],
          tags: ['account', 'settings', 'profile'],
        },
        paragraphs: [
          'You can update your account information at any time by clicking your name in the top-right corner and selecting "Account settings."',
          'From there you can change your display name, update your email address, or update your password.',
          'Changes to your name will be reflected in future bookings. Past bookings will still show the name you used at the time of booking.',
        ],
      },
      {
        id: 'changing-your-password',
        title: 'Changing your password',
        audience: 'seeker',
        keywords: ['password', 'change password', 'reset password', 'forgot password', 'security', 'login trouble'],
        searchMetadata: {
          aiKeywords: ['change password', 'reset password', 'forgot password', 'password reset email', 'login issue', 'can\'t log in', 'locked out', 'new password', 'password requirements', 'strong password', 'account security'],
          intentPhrases: ['how do I change my password', 'I forgot my password', 'how to reset my password', 'I can\'t log in', 'how to make a strong password', 'where do I change my password'],
          tags: ['password', 'security', 'account'],
        },
        paragraphs: [
          'To change your password:',
          '1. Click your name in the top-right corner. 2. Select "Account settings." 3. Click "Change password." 4. Enter your current password and your new password. 5. Click Save.',
          'If you\'ve forgotten your password, click "Forgot password?" on the login page. You\'ll receive an email with a link to reset it.',
          'For security, choose a password that\'s at least 8 characters long and includes a mix of letters and numbers.',
        ],
      },
      {
        id: 'email-preferences',
        title: 'Email preferences',
        audience: 'seeker',
        keywords: ['email', 'notifications', 'emails', 'stop emails', 'email preferences', 'unsubscribe', 'too many emails'],
        searchMetadata: {
          aiKeywords: ['email notifications', 'stop emails', 'unsubscribe', 'too many emails', 'booking confirmation email', 'reminder email', 'cancellation email', 'transactional email', 'email settings', 'notification preferences', 'email frequency'],
          intentPhrases: ['how do I stop getting emails', 'can I unsubscribe from emails', 'what emails will I receive', 'how to change email settings', 'why am I getting emails', 'are the emails mandatory'],
          tags: ['email', 'notifications', 'preferences'],
        },
        paragraphs: [
          'Divine Frameworks sends you emails for important updates related to your sessions:',
          'Booking confirmations: Sent immediately after you book a session. Session reminders: Sent 24 hours before your session. Cancellation and reschedule notifications: Sent when a session is cancelled or rescheduled. Support responses: Replies from the Divine Frameworks team when you contact support.',
          'These are transactional emails related to your bookings and can\'t be turned off — they contain important information about sessions you\'ve paid for.',
          'If you\'re receiving emails you don\'t expect, contact us at support@divineframeworks.com.',
        ],
      },
      {
        id: 'deleting-your-seeker-account',
        title: 'Deleting your account',
        audience: 'seeker',
        keywords: ['delete account', 'remove account', 'close account', 'deactivate', 'cancel account', 'data deletion', 'privacy'],
        searchMetadata: {
          aiKeywords: ['delete my account', 'close account', 'remove data', 'deactivate account', 'cancel account', 'data privacy', 'GDPR', 'right to delete', 'cooling off period', '14 days', 'permanent deletion', 'reactivate account', 'financial records'],
          intentPhrases: ['how do I delete my account', 'can I reactivate after deleting', 'what data is deleted', 'is deletion permanent', 'how to close my account', 'what happens to my booking history'],
          tags: ['deletion', 'privacy', 'account'],
        },
        paragraphs: [
          'If you\'d like to delete your Divine Frameworks account, you can do so from your account settings.',
          'Here\'s what happens when you delete your account:',
          'There\'s a 14-day cooling-off period: Your account is deactivated immediately but not permanently deleted for 14 days. During this time, you can contact us to reactivate it if you change your mind.',
          'Your data is removed: After the cooling-off period, your personal information is permanently deleted. This includes your profile, saved practitioners, and message history.',
          'Financial records are retained: For legal and tax purposes, records of payments and refunds are kept in an anonymized form.',
          'Active bookings: If you have upcoming sessions, please cancel them before deleting your account to ensure you receive any applicable refunds.',
          'To delete your account, go to Account settings and click "Delete account."',
        ],
      },
    ],
  },

  // ── Events (Practitioner) ──
  {
    id: 'events-practitioner',
    slug: 'events-practitioner',
    title: 'Events & Workshops',
    description: 'Create and manage group events, workshops, and retreats',
    Icon: Calendar,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    audience: 'practitioner',
    articles: [
      {
        id: 'creating-events',
        title: 'Creating and managing events',
        audience: 'practitioner',
        keywords: ['events', 'workshop', 'retreat', 'class', 'group session', 'create event', 'event types', 'series', 'multi-day'],
        searchMetadata: {
          aiKeywords: ['event', 'workshop', 'retreat', 'group', 'class'],
          intentPhrases: ['how to create an event', 'set up a workshop', 'host a retreat'],
          tags: ['events', 'creation'],
        },
        paragraphs: [
          'You can create workshops, classes, retreats, and group sessions from your dashboard.',
          'Go to Events → Create Event. Choose your event type (single session, multi-session series, or multi-day), set up pricing, and publish when ready.',
          'Your events will appear on your website and in the Divine Frameworks directory where seekers can discover and register.',
          'Three event types are available: Single session — a one-time event on a specific date. Multi-session series — recurring sessions over multiple weeks (like a 4-week course). Multi-day — consecutive days (like a weekend retreat).',
          'Four pricing options: flat rate, early bird (discounted price until a deadline), deposit (partial payment upfront with remainder due later), or free.',
        ],
      },
      {
        id: 'managing-attendees',
        title: 'Managing event attendees',
        audience: 'practitioner',
        keywords: ['attendees', 'registrations', 'export', 'CSV', 'group message', 'recording', 'remove attendee'],
        searchMetadata: {
          aiKeywords: ['attendee', 'registration', 'export', 'message', 'recording'],
          intentPhrases: ['view attendees', 'export attendee list', 'send group message', 'share recording'],
          tags: ['events', 'attendees'],
        },
        paragraphs: [
          'View and manage your event attendees from the event dashboard.',
          'Go to Events → click "Manage" on your event → Attendees tab. You\'ll see a list of all registered attendees with their payment status.',
          'You can export the attendee list as a CSV, send group messages to all attendees via the Communications tab, and remove attendees if needed (they\'ll receive a full refund).',
          'After your event, you can share a recording with all attendees from the Overview tab. Set an expiry period (7 days, 30 days, or no expiration) and attendees will receive an email with the link.',
        ],
      },
    ],
  },

  // ── Events (Seeker) ──
  {
    id: 'events-seeker',
    slug: 'events-seeker',
    title: 'Events & Workshops',
    description: 'Find and register for group events, workshops, and retreats',
    Icon: Calendar,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    audience: 'seeker',
    articles: [
      {
        id: 'registering-for-events',
        title: 'Registering for events',
        audience: 'seeker',
        keywords: ['register', 'event', 'workshop', 'sign up', 'book event', 'attend'],
        searchMetadata: {
          aiKeywords: ['register', 'event', 'workshop', 'sign up'],
          intentPhrases: ['how to register for an event', 'sign up for a workshop', 'book an event'],
          tags: ['events', 'registration'],
        },
        paragraphs: [
          'Browse upcoming events on the Divine Frameworks directory (Events tab) or on individual practitioner websites.',
          'To register, click "Register" on any event. You\'ll need a Divine Frameworks account. For paid events, payment is processed securely through Stripe.',
          'After registering, you\'ll receive a confirmation email with all the details. For virtual events, your meeting link will be available on the event page after logging in.',
          'You can view all your registered events in My Sessions alongside your 1:1 bookings.',
        ],
      },
      {
        id: 'cancelling-event-registration',
        title: 'Cancelling an event registration',
        audience: 'seeker',
        keywords: ['cancel event', 'event refund', 'cancel registration', 'unregister'],
        searchMetadata: {
          aiKeywords: ['cancel', 'refund', 'registration', 'unregister'],
          intentPhrases: ['how to cancel event registration', 'get a refund for an event', 'cancel my registration'],
          tags: ['events', 'cancellation'],
        },
        paragraphs: [
          'You can cancel your event registration from My Sessions or from the event detail page.',
          'Find the event and click "Cancel registration." For paid events, you\'ll receive a full refund to your original payment method within 5-10 business days.',
          'If the practitioner cancels the event, you\'ll automatically receive a full refund and a notification email.',
        ],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getCategoryBySlug(slug) {
  return HELP_CATEGORIES.find((c) => c.slug === slug) || null;
}

/**
 * Returns categories filtered by user role.
 * @param {'practitioner'|'seeker'} role
 */
export function getCategoriesForRole(role) {
  return HELP_CATEGORIES.filter(
    (cat) => cat.audience === role || cat.audience === 'both'
  );
}

/**
 * Returns a flat array of all articles for a given role, enriched with
 * category metadata (for SmartSearch badge display).
 * @param {'practitioner'|'seeker'} role
 */
export function getArticlesForRole(role) {
  const cats = getCategoriesForRole(role);
  const articles = [];
  for (const cat of cats) {
    for (const article of cat.articles) {
      if (article.audience && article.audience !== role && article.audience !== 'both') continue;
      articles.push({
        ...article,
        categorySlug: cat.slug,
        categoryName: cat.title,
        categoryColor: cat.iconBg === 'bg-purple-100' ? '#F3E8FF' :
                        cat.iconBg === 'bg-indigo-100' ? '#E0E7FF' :
                        cat.iconBg === 'bg-blue-100' ? '#DBEAFE' :
                        cat.iconBg === 'bg-emerald-100' ? '#D1FAE5' :
                        cat.iconBg === 'bg-amber-100' ? '#FEF3C7' :
                        cat.iconBg === 'bg-red-100' ? '#FEE2E2' :
                        cat.iconBg === 'bg-green-100' ? '#D1FAE5' :
                        cat.iconBg === 'bg-cyan-100' ? '#CFFAFE' : '#F3F4F6',
        categoryTextColor: cat.iconColor === 'text-purple-600' ? '#7C3AED' :
                           cat.iconColor === 'text-indigo-600' ? '#4F46E5' :
                           cat.iconColor === 'text-blue-600' ? '#2563EB' :
                           cat.iconColor === 'text-emerald-600' ? '#059669' :
                           cat.iconColor === 'text-amber-600' ? '#D97706' :
                           cat.iconColor === 'text-red-600' ? '#DC2626' :
                           cat.iconColor === 'text-green-600' ? '#059669' :
                           cat.iconColor === 'text-cyan-600' ? '#0891B2' : '#6B7280',
        audienceLabel: article.audience === 'practitioner' ? 'Practitioner' :
                       article.audience === 'seeker' ? 'Seeker' : '',
      });
    }
  }
  return articles;
}
